import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, MessageCircle } from 'lucide-react';
import { companyInfo, zones } from '../data/mock';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

// Configuration Formspree
const FORMSPREE_ENDPOINT = 'https://formspree.io/f/maqdyqqo';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    city: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          city: formData.city,
          _subject: `Nouvelle demande d'intervention - ${formData.city}`
        })
      });

      if (response.ok) {
        setSubmitted(true);
        setTimeout(() => setSubmitted(false), 5000);
        setFormData({ name: '', phone: '', city: '' });
      } else {
        throw new Error('Erreur lors de l\'envoi');
      }
    } catch (err) {
      console.error('Formspree error:', err);
      setError('Une erreur est survenue. Veuillez nous appeler directement.');
    } finally {
      setLoading(false);
    }
  };

  // Formater le numéro pour WhatsApp (format international)
  const whatsappNumber = companyInfo.phone.replace(/\s/g, '').replace(/^0/, '33');
  const whatsappMessage = encodeURIComponent(`Bonjour, je souhaite une intervention rapide. Mon nom: ${formData.name || '[Votre nom]'}, Ville: ${formData.city || '[Votre ville]'}`);

  const contactInfo = [
    {
      icon: Phone,
      label: 'Téléphone',
      value: companyInfo.phone,
      href: `tel:${companyInfo.phone.replace(/\s/g, '')}`,
      highlight: true
    },
    {
      icon: Mail,
      label: 'Email',
      value: companyInfo.email,
      href: `mailto:${companyInfo.email}`
    },
    {
      icon: MapPin,
      label: 'Adresse',
      value: companyInfo.address,
      href: `https://maps.google.com/?q=${encodeURIComponent(companyInfo.address)}`
    },
    {
      icon: Clock,
      label: 'Disponibilité',
      value: companyInfo.availability
    }
  ];

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-sky-700 via-sky-800 to-teal-700 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 right-20 w-72 h-72 bg-lime-400/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-sky-400/10 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 relative">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block bg-lime-400/20 text-lime-300 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Contact
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Besoin d'une <span className="text-lime-400">intervention</span> ?
          </h2>
          <p className="text-sky-200 max-w-2xl mx-auto text-lg">
            Contactez-nous pour un devis gratuit ou une intervention d'urgence
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-8">Nos coordonnées</h3>
            
            <div className="space-y-4 mb-8">
              {contactInfo.map((info, index) => {
                const Icon = info.icon;
                return (
                  <div
                    key={index}
                    className={`flex items-start gap-4 p-4 rounded-xl transition-all duration-300 ${
                      info.highlight 
                        ? 'bg-lime-500 text-gray-900' 
                        : 'bg-white/10 backdrop-blur-sm hover:bg-white/20'
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      info.highlight ? 'bg-gray-900/10' : 'bg-white/10'
                    }`}>
                      <Icon size={24} className={info.highlight ? 'text-gray-900' : 'text-lime-400'} />
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${
                        info.highlight ? 'text-gray-900/70' : 'text-sky-200'
                      }`}>
                        {info.label}
                      </p>
                      {info.href ? (
                        <a 
                          href={info.href}
                          target={info.href.startsWith('http') ? '_blank' : undefined}
                          rel={info.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                          className={`font-semibold hover:underline ${
                            info.highlight ? 'text-gray-900 text-xl' : 'text-white'
                          }`}
                        >
                          {info.value}
                        </a>
                      ) : (
                        <p className={`font-semibold ${
                          info.highlight ? 'text-gray-900' : 'text-white'
                        }`}>
                          {info.value}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Urgence CTA */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <p className="text-lime-300 font-semibold mb-2">Urgence débouchage ?</p>
              <p className="text-sky-100 mb-4">
                Nous intervenons en moins de 2 heures sur Cannes et environs.
              </p>
              <a href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}>
                <Button className="w-full bg-lime-500 hover:bg-lime-400 text-gray-900 font-bold py-4 rounded-xl text-lg">
                  <Phone size={22} className="mr-2" />
                  Appeler maintenant
                </Button>
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-3xl p-8 shadow-2xl">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Intervention Immédiate</h3>
              <p className="text-sky-600 font-semibold">Appel immédiat pour une intervention instantanée</p>
            </div>
            
            {submitted ? (
              <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="contact-success-message">
                <div className="w-16 h-16 bg-lime-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="text-lime-600" size={32} />
                </div>
                <h4 className="text-xl font-bold text-gray-800 mb-2">Demande envoyée !</h4>
                <p className="text-gray-600">Nous vous rappelons dans les plus brefs délais.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                {error && (
                  <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm" data-testid="contact-error">
                    {error}
                  </div>
                )}
                
                <div>
                  <Label htmlFor="name" className="text-gray-700 font-medium">Nom *</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Votre nom"
                    className="mt-1 border-slate-200 focus:border-sky-500 focus:ring-sky-500 h-12 text-lg"
                    data-testid="contact-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="text-gray-700 font-medium">Numéro de téléphone *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="06 XX XX XX XX"
                    className="mt-1 border-slate-200 focus:border-sky-500 focus:ring-sky-500 h-12 text-lg"
                    data-testid="contact-phone-input"
                  />
                </div>

                <div>
                  <Label htmlFor="city" className="text-gray-700 font-medium">Ville *</Label>
                  <select
                    id="city"
                    name="city"
                    required
                    value={formData.city}
                    onChange={handleChange}
                    className="mt-1 w-full px-3 py-3 border border-slate-200 rounded-md focus:border-sky-500 focus:ring-sky-500 focus:outline-none bg-white h-12 text-lg"
                    data-testid="contact-city-select"
                  >
                    <option value="">Sélectionnez votre ville</option>
                    {zones.map((zone, index) => (
                      <option key={index} value={zone}>{zone}</option>
                    ))}
                  </select>
                </div>

                <Button 
                  type="submit" 
                  disabled={loading}
                  className="w-full bg-lime-500 hover:bg-lime-400 text-gray-900 font-bold py-4 rounded-xl text-lg h-14 disabled:opacity-50"
                  data-testid="contact-submit-btn"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Envoi en cours...
                    </span>
                  ) : (
                    <>
                      <Send size={20} className="mr-2" />
                      Demander un rappel immédiat
                    </>
                  )}
                </Button>

                {/* Séparateur */}
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-gray-500">ou contactez-nous directement</span>
                  </div>
                </div>

                {/* Boutons d'action directe */}
                <div className="grid grid-cols-2 gap-3">
                  <a
                    href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                    className="flex items-center justify-center gap-2 bg-sky-600 hover:bg-sky-700 text-white font-bold py-3 px-4 rounded-xl transition-colors"
                    data-testid="contact-call-btn"
                  >
                    <Phone size={20} />
                    <span>Appeler</span>
                  </a>
                  <a
                    href={`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded-xl transition-colors"
                    data-testid="contact-whatsapp-btn"
                  >
                    <MessageCircle size={20} />
                    <span>WhatsApp</span>
                  </a>
                </div>

                <p className="text-xs text-gray-500 text-center mt-4">
                  * Nous vous rappelons sous 5 minutes pendant nos heures d'ouverture
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;